import { CHECKLIST_ITEM_TYPE } from '../constants/tasksV2';

/**
 * Where a data-capture checklist item's VALUE actually lives.
 *
 * There is no `value` field on a checklist item — not on the ChecklistItem model and not in
 * ChecklistItemSerializer.Meta.fields. A `PATCH {value}` is therefore accepted with 200 and
 * silently discarded; the backend's own manifest test documents it
 * (apps/tasks/tests/test_checklist_action_manifest.py::_assert_value_not_persisted —
 * "Proves the silent-drop bug"). That is how staff could set a payment method, a deposit
 * amount, a yes/no answer, a clinician or a date, see no error, and find nothing saved.
 *
 * The sanctioned home is `response_payload` on the /complete/ call, and
 * apps/tasks/payload_schemas.py already whitelists one key per item type. This module is the
 * single source of that mapping and is used for BOTH writing (inline editors) and reading
 * (table cells, editors), so the write key and the read key cannot drift apart.
 */

// item_type → the response_payload key that carries its value.
// Keys MUST stay in sync with RESPONSE_PAYLOAD_SCHEMAS in apps/tasks/payload_schemas.py —
// anything else is flagged as an unknown key (warned in Sentry, rejected outright when
// TASKS_V2_PAYLOAD_VALIDATION_STRICT is on).
export const CHECKLIST_VALUE_PAYLOAD_KEY = {
    [CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE]: 'answer',
    [CHECKLIST_ITEM_TYPE.CONFIRMATION]: 'answer',
    [CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT]: 'amount',
    [CHECKLIST_ITEM_TYPE.STAFF_PICKER]: 'staff_user_id',
    [CHECKLIST_ITEM_TYPE.DATE_FIELD]: 'date',
    [CHECKLIST_ITEM_TYPE.SIMPLE_DATE]: 'date',
    [CHECKLIST_ITEM_TYPE.FREE_TEXT]: 'text',
    [CHECKLIST_ITEM_TYPE.MULTIPLE_CHOICE]: 'choices',
    // PAYMENT_METHOD carries two different things and they must not be conflated:
    //   mode_of_payment — HOW the patient is paying (In full / GoCardless / Finance / PAYG),
    //                     which is what the "Payment Method" column header asks for.
    //   payment_type    — the type of ONE recorded LeadTreatmentPayment (DEPOSIT / FINANCE /
    //                     …), written by the table's payments ledger.
    // The picker writes mode_of_payment; reads accept either (see readChecklistItemValue).
    [CHECKLIST_ITEM_TYPE.PAYMENT_METHOD]: 'mode_of_payment',
};

// Legacy / alternative keys accepted when READING, newest-first. Covers payloads written
// before this mapping existed and the two PAYMENT_METHOD vocabularies above.
const READ_FALLBACK_KEYS = {
    [CHECKLIST_ITEM_TYPE.YES_NO_TOGGLE]: ['confirmation_answer'],
    [CHECKLIST_ITEM_TYPE.CONFIRMATION]: ['confirmation_answer'],
    [CHECKLIST_ITEM_TYPE.PAYMENT_METHOD]: ['payment_type'],
};

const isEmpty = (v) => v == null || v === '';

/**
 * Build the response_payload for "the user set this item's value".
 * Returns {} for types with no value key, so callers can spread it unconditionally.
 */
export function buildChecklistValuePayload(itemType, value) {
    const key = CHECKLIST_VALUE_PAYLOAD_KEY[itemType];
    if (!key) return {};
    // Currency: store a real number when the input is numeric so reporting can sum it,
    // while preserving deliberate non-numeric entries ("N/A" is allowed by the column).
    if (itemType === CHECKLIST_ITEM_TYPE.CURRENCY_AMOUNT && value !== '' && Number.isFinite(Number(value))) {
        return { [key]: Number(value) };
    }
    return { [key]: value };
}

/**
 * Read the stored value of a data-capture item, wherever it was written.
 * `item` is either a checklist item or a table cellData — both carry response_payload.
 */
export function readChecklistItemValue(itemType, item) {
    if (!item) return '';
    const payload = item.response_payload || {};
    const keys = [CHECKLIST_VALUE_PAYLOAD_KEY[itemType], ...(READ_FALLBACK_KEYS[itemType] || [])].filter(Boolean);
    const hit = keys.find((k) => !isEmpty(payload[k]));
    if (hit) return payload[hit];
    // Last resort: a `value` set locally by an optimistic update. Never persisted server-side
    // — see the module docstring — but harmless to honour when it is present in memory.
    return isEmpty(item.value) ? '' : item.value;
}

export default readChecklistItemValue;
